if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        const artwork = this.form.querySelector('[data-sg-artwork]');
        if (artwork) artwork.addEventListener('change', async () => {
          const file = artwork.files[0];
          sgArtworkStatus(artwork, file ? 'Checking file...' : '', 'ready');
          if (!file) return;
          try {
            await sgValidateArtwork(file);
            if (artwork.files[0] === file) sgArtworkStatus(artwork, 'File ready. Click Add to cart to upload it.', 'ready');
          } catch (error) {
            if (artwork.files[0] === file) sgArtworkStatus(artwork, error.message, 'error');
          }
        });
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const artwork = this.form.querySelector('[data-sg-artwork]');
        const artworkFile = artwork?.files[0];
        if (artworkFile) {
          try {
            await sgValidateArtwork(artworkFile);
          } catch (error) {
            sgArtworkStatus(artwork, error.message, 'error');
            this.handleErrorMessage(error.message);
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading__spinner').classList.add('hidden');
            artwork.focus();
            return;
          }
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const swatchKey = 'properties[color-swatches-1]';
        if (artwork && formData.has(swatchKey)) {
          formData.set('properties[Colour Swatches]', formData.getAll(swatchKey).join(', '));
          formData.delete(swatchKey);
        }
        if (artworkFile) formData.set('properties[Artwork]', artworkFile);
        else if (artwork) formData.delete('properties[Artwork]');
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        if (artworkFile) artwork.disabled = true;
        const request = artworkFile
          ? sgUploadArtwork(routes.cart_add_url, config, artwork)
          : fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
        request
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              if (artworkFile) sgArtworkStatus(artwork, response.description || 'Upload failed. Please try again.', 'error');
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }
            if (artworkFile) {
              if (response.properties?.Artwork) sgArtworkStatus(artwork, 'Upload successful. Your image is attached to this cart item.', 'success', 100);
              else sgArtworkStatus(artwork, 'The item was added, but its image could not be confirmed. Check your cart before continuing.', 'error');
            }
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            const message = 'We could not confirm the upload. Check your cart before trying again.';
            if (artworkFile) sgArtworkStatus(artwork, message, 'error');
            this.handleErrorMessage(artworkFile ? message : 'Your item could not be added. Please try again.');
          })
          .finally(() => {
            if (artwork) artwork.disabled = false;
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

async function sgValidateArtwork(file) {
  const extension = file.name.split('.').pop().toLowerCase();
  if (!['png', 'jpg', 'jpeg', 'svg', 'eps'].includes(extension)) {
    throw new Error('Please choose a PNG, JPG, SVG or EPS file.');
  }
  if (!file.size || file.size > 20 * 1024 * 1024) {
    throw new Error('Please choose a file between 1 byte and 20 MB.');
  }
  const bytes = new Uint8Array(await file.slice(0, 512).arrayBuffer());
  const starts = (signature) => signature.every((byte, index) => bytes[index] === byte);
  let valid = false;
  if (extension === 'png') valid = starts([137, 80, 78, 71, 13, 10, 26, 10]);
  if (extension === 'jpg' || extension === 'jpeg') valid = starts([255, 216, 255]);
  if (extension === 'eps') {
    const header = new TextDecoder().decode(bytes);
    valid = (header.startsWith('%!PS-Adobe-') && header.includes('EPSF-')) || starts([197, 208, 211, 198]);
  }
  if (extension === 'svg') {
    const svg = new DOMParser().parseFromString(await file.text(), 'image/svg+xml');
    valid = !svg.querySelector('parsererror') && svg.documentElement.localName === 'svg';
    if (valid) {
      for (const element of svg.querySelectorAll('*')) {
        if (['script', 'foreignObject'].includes(element.localName)) valid = false;
        for (const attribute of element.attributes) {
          if (/^on/i.test(attribute.name) || /^javascript:/i.test(attribute.value.trim())) valid = false;
        }
      }
    }
  }
  if (!valid) throw new Error('This file does not appear to be a supported image. Please choose another PNG, JPG, SVG or EPS.');
}


function sgArtworkStatus(input, message, state, percent) {
  const root = input.closest('.sg-native-artwork');
  const status = root.querySelector('[data-sg-upload-status]');
  const progress = root.querySelector('[data-sg-upload-progress]');
  status.textContent = message;
  status.dataset.state = state;
  progress.hidden = !['uploading', 'success'].includes(state);
  if (typeof percent === 'number') progress.value = percent;
  else progress.removeAttribute('value');
}

function sgUploadArtwork(url, config, input) {
  sgArtworkStatus(input, 'Uploading image... 0%', 'uploading', 0);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.timeout = 120000;
    Object.entries(config.headers).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    xhr.upload.onprogress = (event) => {
      const percent = event.lengthComputable ? Math.round(event.loaded / event.total * 100) : undefined;
      sgArtworkStatus(input, percent === 100 ? 'Upload sent. Confirming your cart item...' : `Uploading image...${percent === undefined ? '' : ` ${percent}%`}`, 'uploading', percent);
    };
    xhr.onload = () => {
      try {
        const result = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) resolve(result);
        else resolve({ ...result, status: xhr.status });
      } catch (error) { reject(error); }
    };
    xhr.onerror = xhr.ontimeout = xhr.onabort = () => reject(new Error('Upload could not be confirmed.'));
    xhr.send(config.body);
  });
}

